import { ImageError, type Result } from "@inrev/core";
import { Card, ClickableIcon } from "@portal/components/primitives";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { type RefObject, useEffect, useRef, useState } from "react";
import { match, P } from "ts-pattern";
import { downloadFileFromDataBlob } from "../../../../../../../utils/download";
import {
	assistantCopiedLabel,
	assistantCopyChartImageFailed,
	assistantCopyChartImageLabel,
	assistantMakeChartImageFailed,
	assistantSaveChartImageLabel,
} from "../../assistantCopy";
import {
	ClipboardWriteError,
	type CopyOrgChartImageError,
	canCopyImages,
	copyOrgChartCardPng,
	omittedFromOrgChartImage,
	orgChartCardToPng,
} from "./orgChartImage";

const COPIED_RESET_MS = 1200;

const chartImageFailedMessage = (error: CopyOrgChartImageError): string =>
	match(error)
		.with(P.instanceOf(ImageError), () => assistantMakeChartImageFailed)
		.with(P.instanceOf(ClipboardWriteError), () => assistantCopyChartImageFailed)
		.exhaustive();

/** Copies or saves the chart card as a PNG. */
export const OrgChartImageActions = ({
	cardRef,
	fileName,
}: {
	cardRef: RefObject<HTMLElement | null>;
	fileName: string;
}) => {
	const { showErrorFlag } = useFlags();
	const [copied, setCopied] = useState(false);
	const [busy, setBusy] = useState(false);
	const busyRef = useRef(false);
	const resetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => () => clearTimeout(resetTimeout.current), []);

	const runCapture = async <A,>(
		capture: (card: HTMLElement) => Promise<Result<CopyOrgChartImageError, A>>,
		onCaptured: (value: A) => void,
	) => {
		const card = cardRef.current;
		if (card === null || busyRef.current) return;
		busyRef.current = true;
		setBusy(true);
		const captured = await capture(card);
		busyRef.current = false;
		setBusy(false);
		captured.match(onCaptured, (error) =>
			showErrorFlag({ message: chartImageFailedMessage(error) }),
		);
	};

	const showCopied = () => {
		setCopied(true);
		clearTimeout(resetTimeout.current);
		resetTimeout.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
	};

	const copyImage = () => runCapture(copyOrgChartCardPng, showCopied);

	const saveImage = () =>
		runCapture(orgChartCardToPng, (blob) => downloadFileFromDataBlob(blob, fileName));

	const copyLabel = copied ? assistantCopiedLabel : assistantCopyChartImageLabel;

	return (
		<Card.Actions
			className="shrink-0"
			data-testid="assistant-orgchart-image-actions"
			{...omittedFromOrgChartImage}
		>
			{canCopyImages() && (
				<ClickableIcon
					aria-label={copyLabel}
					color={copied ? "confirm" : "tertiary"}
					data-testid="assistant-orgchart-copy-image"
					disabled={busy}
					onClick={() => void copyImage()}
					size="mini"
					tooltip={copyLabel}
					type={copied ? "check" : "clipboard"}
				/>
			)}
			<ClickableIcon
				aria-label={assistantSaveChartImageLabel}
				color="tertiary"
				data-testid="assistant-orgchart-save-image"
				disabled={busy}
				onClick={() => void saveImage()}
				size="mini"
				tooltip={assistantSaveChartImageLabel}
				type="arrow-down-tray"
			/>
		</Card.Actions>
	);
};
